import * as THREE from 'three';
import type { RideId } from '../../content/rides';
import { box, cyl, M, mat, mesh, sphere, texMat } from '../materials';
import { camoTexture, signTexture } from '../textures';
import { buildCar, type BuiltCar } from './vehicles';

/**
 * Everything you can drive, built facing +x with the origin on the ground under its
 * middle, like buildCar. `onBlocks` only applies to the Camaro.
 */
export function buildRide(id: RideId, opts: { onBlocks?: boolean } = {}): BuiltCar {
  switch (id) {
    case 'camaro':
      return buildCar({
        kind: 'camaro',
        color: '#7a1f2b',
        hood: '#8d8f8a',
        door: '#3d5a80',
        onBlocks: opts.onBlocks,
      });
    case 'lifted':
      return lifted();
    case 'donk':
      return donk();
    case 'elcamino':
      return elCamino();
    case 'crownvic':
      return crownVic();
    case 'van':
      return muralVan();
    case 'atv':
      return threeWheeler();
    case 'mower':
      return ridingMower();
  }
}

function wheel(r: number, w: number, hub: THREE.Material, x: number, z: number, y = r): THREE.Mesh {
  const t = cyl(r, r, w, M.rubber, x, y, z, 16);
  t.rotation.x = Math.PI / 2;
  t.add(cyl(r * 0.62, r * 0.62, 0.02, hub, 0, Math.sign(z) * (w / 2 + 0.005), 0, 12, { cast: false }));
  return t;
}

function shell(): { group: THREE.Group; body: THREE.Group; rear: THREE.Group } {
  const group = new THREE.Group();
  const body = new THREE.Group();
  const rear = new THREE.Group();
  group.add(body);
  body.add(rear);
  return { group, body, rear };
}

function lifted(): BuiltCar {
  const { group, body, rear } = shell();
  const paint = texMat(camoTexture(), { rough: 0.7 });
  const trim = M.black;
  const lift = 0.75;
  body.position.y = lift;
  // Frame and lift kit showing under the body.
  body.add(box(4.6, 0.18, 1.2, trim, 0, 0.24, 0));
  for (const x of [-1.75, 1.75])
    for (const z of [-0.55, 0.55]) body.add(cyl(0.06, 0.06, 0.6, mat('#d23a2a'), x, 0.05, z, 6));
  body.add(box(1.6, 0.62, 2.0, paint, 1.9, 0.75, 0));
  body.add(box(1.5, 0.98, 2.0, paint, 0.4, 0.94, 0));
  // Glass pokes past the cab's nose and sides but stays under the roof, so nothing is flush.
  body.add(box(1.42, 0.46, 2.04, M.darkGlass, 0.46, 1.18, 0, { cast: false }));
  body.add(box(1.28, 0.06, 1.94, paint, 0.4, 1.455, 0));
  body.add(box(2.1, 0.18, 2.0, paint, -1.4, 0.52, 0));
  for (const s of [-1, 1]) body.add(box(2.1, 0.5, 0.08, paint, -1.4, 0.86, s * 0.96));
  body.add(box(0.08, 0.5, 2.0, paint, -2.42, 0.86, 0));
  // Light bar, bull bar and a CB antenna.
  body.add(box(0.2, 0.14, 1.8, trim, 0.5, 1.56, 0));
  for (let i = 0; i < 6; i++)
    body.add(
      box(0.03, 0.08, 0.2, mat('#fff8d0', { emissive: '#d0c070' }), 0.61, 1.56, -0.75 + i * 0.3, {
        cast: false,
      }),
    );
  body.add(box(0.12, 0.5, 1.7, M.black, 2.78, 0.62, 0));
  body.add(box(0.12, 0.08, 1.9, M.black, 2.78, 0.88, 0));
  body.add(cyl(0.012, 0.012, 1.4, M.steel, -0.4, 1.95, 0.9, 4));
  for (const z of [-0.6, 0.6])
    body.add(box(0.05, 0.14, 0.3, mat('#fff3c0', { emissive: '#7a6a30' }), 2.72, 0.72, z, { cast: false }));
  // Mud flaps.
  for (const s of [-1, 1]) body.add(box(0.04, 0.5, 0.4, trim, -2.05, 0.05, s * 0.85));
  rear.position.set(-2.5, 0.42, 0);
  const wheels: THREE.Mesh[] = [];
  const hub = mat('#c9ccd0', { metal: 0.6, rough: 0.35 });
  for (const x of [-1.75, 1.75])
    for (const z of [-1, 1]) {
      const w = wheel(0.62, 0.48, hub, x, z * 1.05);
      wheels.push(w);
      group.add(w);
    }
  return { group, wheels, body, rear, length: 5.6, width: 2.3 };
}

function donk(): BuiltCar {
  const { group, body, rear } = shell();
  const paint = mat('#9b1b8f', { rough: 0.15, metal: 0.65 });
  body.position.y = 0.42;
  const chrome = M.chrome;
  body.add(box(5.4, 0.55, 1.9, paint, 0, 0.55, 0));
  body.add(box(1.7, 0.08, 1.8, paint, 1.75, 0.86, 0));
  body.add(box(2.4, 0.5, 1.72, M.darkGlass, -0.35, 1.07, 0, { cast: false }));
  body.add(box(2.2, 0.06, 1.66, paint, -0.4, 1.34, 0));
  body.add(box(0.12, 0.2, 1.95, chrome, 2.75, 0.42, 0));
  body.add(box(0.12, 0.2, 1.95, chrome, -2.75, 0.42, 0));
  for (const s of [-1, 1]) body.add(box(5.0, 0.05, 0.02, chrome, 0, 0.6, s * 0.955, { cast: false }));
  for (const z of [-0.6, 0.6])
    body.add(box(0.05, 0.14, 0.3, mat('#fff3c0', { emissive: '#7a6a30' }), 2.72, 0.62, z, { cast: false }));
  for (const z of [-0.6, 0.6])
    body.add(box(0.05, 0.12, 0.35, mat('#c4202a', { emissive: '#5a0000' }), -2.72, 0.65, z, { cast: false }));
  rear.position.set(-2.82, 0.42, 0);
  const wheels: THREE.Mesh[] = [];
  for (const x of [-1.65, 1.65])
    for (const z of [-1, 1]) {
      // 26s: almost all rim, rubber-band tires.
      const t = cyl(0.66, 0.66, 0.3, M.rubber, x, 0.66, z * 0.98, 18);
      t.rotation.x = Math.PI / 2;
      t.add(cyl(0.6, 0.6, 0.02, chrome, 0, z * 0.16, 0, 18, { cast: false }));
      wheels.push(t);
      group.add(t);
    }
  return { group, wheels, body, rear, length: 5.6, width: 2.1 };
}

function elCamino(): BuiltCar {
  const { group, body, rear } = shell();
  const paint = mat('#c9a227', { rough: 0.6, metal: 0.2 });
  body.add(box(5.0, 0.5, 1.82, paint, 0, 0.58, 0));
  body.add(box(1.6, 0.04, 1.6, mat('#7d7f7a', { rough: 0.95 }), 1.65, 0.85, 0, { cast: false }));
  body.add(box(1.25, 0.48, 1.7, M.darkGlass, 0.3, 1.05, 0, { cast: false }));
  body.add(box(1.12, 0.06, 1.66, paint, 0.25, 1.32, 0));
  // The bed, with a cooler in it.
  for (const s of [-1, 1]) body.add(box(1.9, 0.28, 0.07, paint, -1.5, 0.95, s * 0.875));
  body.add(box(0.07, 0.28, 1.82, paint, -2.47, 0.95, 0));
  body.add(box(0.6, 0.38, 0.4, mat('#d6452f'), -1.2, 1.0, 0.3));
  body.add(box(0.12, 0.16, 1.85, M.chrome, 2.55, 0.42, 0));
  for (const z of [-0.6, 0.6])
    body.add(box(0.05, 0.14, 0.3, mat('#fff3c0', { emissive: '#7a6a30' }), 2.52, 0.62, z, { cast: false }));
  rear.position.set(-2.52, 0.42, 0);
  const wheels: THREE.Mesh[] = [];
  const hub = mat('#9aa0a6', { metal: 0.5 });
  for (const x of [-1.55, 1.55])
    for (const z of [-1, 1]) {
      const w = wheel(0.34, 0.26, hub, x, z * 0.88);
      wheels.push(w);
      group.add(w);
    }
  return { group, wheels, body, rear, length: 5.0, width: 1.9 };
}

function crownVic(): BuiltCar {
  const car = buildCar({ kind: 'sedan', color: '#e8e8e6' });
  const { body } = car;
  // Faded black doors where the decals came off.
  for (const z of [-0.9, 0.9])
    body.add(box(1.6, 0.42, 0.03, mat('#2b2b2e', { rough: 0.8 }), 0, 0.68, z, { cast: false }));
  body.add(box(0.12, 0.5, 1.2, M.black, 2.48, 0.6, 0));
  body.add(box(0.12, 0.08, 1.5, M.black, 2.48, 0.84, 0));
  body.add(sphere(0.09, M.chrome, 0.95, 1.2, 0.92, 8));
  body.add(cyl(0.012, 0.012, 1.0, M.steel, -1.6, 1.4, -0.4, 4));
  return car;
}

function muralVan(): BuiltCar {
  const car = buildCar({ kind: 'van', color: '#2b3a5c' });
  const tex = signTexture({
    w: 512,
    h: 160,
    bg: '#1a2340',
    fg: '#d9e2f2',
    gap: 0,
    lines: [{ text: ':full-moon: :wolf: :pine::pine:', size: 110, font: 'Rye' }],
  });
  for (const s of [-1, 1]) {
    const m = mesh(new THREE.PlaneGeometry(2.6, 0.82), texMat(tex), -0.6, 1.0, s * 0.965, { cast: false });
    m.rotation.y = s > 0 ? 0 : Math.PI;
    car.body.add(m);
  }
  // A bubble window, as the law requires.
  const bubble = sphere(0.3, M.darkGlass, -1.9, 1.3, 0.965, 10, { cast: false });
  bubble.scale.set(1, 1, 0.25);
  car.body.add(bubble);
  return car;
}

function threeWheeler(): BuiltCar {
  const { group, body, rear } = shell();
  const red = mat('#c62828', { rough: 0.45, metal: 0.2 });
  body.add(box(1.1, 0.3, 0.55, red, 0.1, 0.72, 0));
  body.add(box(0.7, 0.14, 0.42, M.black, -0.25, 0.92, 0));
  body.add(box(0.5, 0.08, 1.05, red, -0.75, 0.86, 0));
  body.add(box(0.4, 0.08, 0.4, red, 0.85, 0.95, 0));
  body.add(cyl(0.025, 0.025, 0.7, M.chrome, 0.75, 1.12, 0, 6).rotateX(Math.PI / 2));
  body.add(cyl(0.03, 0.03, 0.55, M.chrome, 0.8, 0.75, 0, 6).rotateZ(0.4));
  body.add(box(0.04, 0.1, 0.16, mat('#fff3c0', { emissive: '#7a6a30' }), 1.06, 0.95, 0, { cast: false }));
  rear.position.set(-1.0, 0.6, 0);
  const wheels: THREE.Mesh[] = [];
  const hub = mat('#e8e2d0');
  // Balloon tires: one up front, two in back.
  wheels.push(wheel(0.4, 0.36, hub, 0.95, 0.001));
  for (const z of [-1, 1]) wheels.push(wheel(0.42, 0.42, hub, -0.75, z * 0.48));
  for (const w of wheels) group.add(w);
  return { group, wheels, body, rear, length: 2.4, width: 1.4 };
}

function ridingMower(): BuiltCar {
  const { group, body, rear } = shell();
  const green = mat('#2f7d32', { rough: 0.5 });
  const yellow = mat('#e8c21a', { rough: 0.5 });
  body.add(box(1.7, 0.3, 0.9, green, 0, 0.5, 0));
  body.add(box(0.7, 0.32, 0.86, green, 0.62, 0.78, 0));
  body.add(box(1.2, 0.1, 1.1, mat('#1f5a22'), 0.1, 0.3, 0));
  body.add(box(0.45, 0.12, 0.5, M.black, -0.35, 0.78, 0));
  body.add(box(0.1, 0.4, 0.5, M.black, -0.58, 0.98, 0));
  body.add(cyl(0.02, 0.02, 0.45, M.black, 0.3, 1.0, 0, 6).rotateZ(0.5));
  body.add(cyl(0.12, 0.12, 0.03, M.black, 0.42, 1.2, 0, 12).rotateZ(0.5));
  for (const s of [-1, 1]) body.add(box(1.6, 0.04, 0.02, yellow, 0, 0.55, s * 0.46, { cast: false }));
  // A cup holder, occupied.
  body.add(cyl(0.035, 0.035, 0.12, mat('#c0392b', { metal: 0.6 }), -0.2, 0.92, 0.3, 8));
  rear.position.set(-0.9, 0.45, 0);
  const wheels: THREE.Mesh[] = [];
  const hub = yellow;
  for (const z of [-1, 1]) {
    wheels.push(wheel(0.22, 0.16, hub, 0.65, z * 0.42));
    wheels.push(wheel(0.33, 0.24, hub, -0.55, z * 0.46));
  }
  for (const w of wheels) group.add(w);
  return { group, wheels, body, rear, length: 2.0, width: 1.1 };
}
